import React, { useState, useEffect, useCallback } from "react";
import "./tasks.scss";
import PlantHeader from "../plantHeader/plantHeader";
import Menu from "../menu/menu";
import Search from "../search/search";
import {
  getFormattedDate,
  HOST_URL,
  LOCAL_STORAGE_USER_KEY,
  LOCAL_STORAGE_PROJECT,
} from "../../consts";
import addIcon from "./add-icon.svg";
import EditTask from "../editTask/editTask";
import Loader from "../loader/loader";
import Modal from "../modal/modal";
import { withNavigation } from "../withNavigate";
import { TimeZone } from "../utils";
import Attachment from "../attachment/attachment";
import {
  getCurrentProject,
  redirectIfNoProject,
} from "../../utils/projectUtils";
import ApiHandler from "../api";

const TasksComponent = ({ navigate }) => {
  // Get user and project from localStorage like in robot.js
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);
  const [timeZone, setTimeZone] = useState(null);

  // Local state for tasks data
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showEditTask, setShowEditTask] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDeleteTask, setShowDeleteTask] = useState(false);
  const [attachment, setAttachment] = useState(undefined);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const apiHandler = new ApiHandler();

  // Initialize newTask
  const newTask = project
    ? {
        projectId: project._id,
        created: Date.now(),
        dueDate: Date.now(),
        status: "",
        taskType: "",
        description: "",
        createdBy: {},
        closedBy: {},
      }
    : null;

  // Initialize user and project from localStorage
  useEffect(() => {
    const initializeData = () => {
      // Get initial data from localStorage
      const storedUser = JSON.parse(
        localStorage.getItem(LOCAL_STORAGE_USER_KEY) || "null"
      );
      const storedProject = getCurrentProject();

      if (!storedProject) {
        navigate("/projects");
        return;
      }

      setUser(storedUser);
      setProject(storedProject);
      setTimeZone(new TimeZone(storedProject));
    };

    initializeData();
  }, [navigate]);

  // Load tasks when project is available
  useEffect(() => {
    if (!project || !project._id) {
      return;
    }

    loadTasks();
  }, [project, search, startDate, endDate]);

  const loadTasks = useCallback(async () => {
    if (!project || !project._id) return;

    setIsLoading(true);
    try {
      const data = await apiHandler.getTasks(
        project._id,
        search,
        startDate,
        endDate
      );
      setTasks(data.tasks || []);
    } catch (error) {
      console.error("Failed to load tasks:", error);
      setTasks([]);
    } finally {
      setIsLoading(false);
    }
  }, [project, search, startDate, endDate, apiHandler]);

  const handleTaskSelected = (task) => {
    setSelectedTask({
      ...task,
      dueDate: new Date(task.dueDate),
      created: new Date(task.created),
    });
    setShowEditTask(true);
  };

  const handleCloseEditTask = async (showEditTask) => {
    const wasOpen = !showEditTask;
    setShowEditTask(showEditTask);
    // Refresh tasks list when modal closes (after task was created/updated)
    if (wasOpen) {
      // Add a small delay to ensure Redux actions have completed
      setTimeout(async () => {
        await loadTasks();
      }, 100);
    }
  };

  const handleDateChanged = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (start && end && project && project._id) {
      loadTasks();
    }
  };

  const handleSearchClicked = (searchTerm) => {
    setSearch(searchTerm);
    if (project && project._id) {
      loadTasks();
    }
  };

  const handleCreateNewTask = () => {
    if (!newTask) {
      return;
    }

    setSelectedTask({ ...newTask });
    setShowEditTask(true);
  };

  const handleCloseDeleteTaskModal = () => {
    setShowDeleteTask(false);
    setShowEditTask(true);
  };

  const handleDeleteTask = async () => {
    if (!selectedTask || !selectedTask._id) return;

    setShowDeleteTask(false);
    setShowEditTask(false);

    try {
      await apiHandler.deleteTask(selectedTask._id);
      // Refresh tasks after deletion
      await loadTasks();
    } catch (error) {
      console.error("Failed to delete task:", error);
    }
  };

  const handleAttachmentClicked = (e, attachment) => {
    e.stopPropagation();
    window.open(attachment.url, "_blank");
  };

  const getRowText = (task) => {
    if (!task.row) return;

    if (task.locationOnRow && task.locationOnRow >= 0)
      return `${task.row.number} (${Math.round(task.locationOnRow / 1000)}m)`;
    else return task.row.number;
  };

  // If no project exists, don't render
  if (!project) {
    return null;
  }

  return (
    <div className="page">
      {isLoading ? <Loader /> : null}

      {showEditTask && project ? (
        <EditTask
          closeEditTask={() => handleCloseEditTask(false)}
          task={selectedTask}
          project={project}
          deleteTask={() => {
            setShowDeleteTask(true);
            setShowEditTask(false);
          }}
        />
      ) : null}

      {showDeleteTask ? (
        <Modal title={`Delete this task?`} onClose={handleCloseDeleteTaskModal}>
          <div className="buttons-wrapper">
            <div className="button cancel" onClick={handleCloseDeleteTaskModal}>
              Cancel
            </div>
            <div className="button" onClick={handleDeleteTask}>
              Delete forever
            </div>
          </div>
        </Modal>
      ) : null}

      {attachment ? (
        <Attachment
          src={attachment}
          closeAttachment={() => setAttachment(undefined)}
        />
      ) : null}

      <div className="menu-wrapper">
        <Menu />
      </div>
      <div className="content">
        <PlantHeader title={"Tasks"} secondaryText={project.projectName} />
        <div className="tasks">
          <div className="top">
            <Search onSearch={handleSearchClicked} placeholder="search tasks" />

            {user?.role !== "projectOwner" ? (
              <div className="download" onClick={handleCreateNewTask}>
                <img src={addIcon} />
                CREATE TASK
              </div>
            ) : null}
          </div>
          <table>
            <tbody>
              <tr>
                <th>Created</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Task Type</th>
                <th>Description</th>
                <th>Closed</th>
                <th>Attachments</th>
              </tr>
              {tasks.map((task, index) => {
                return (
                  <tr key={index} onClick={() => handleTaskSelected(task)}>
                    <td className="date">
                      {task.createdBy.fullName}
                      <br />
                      {timeZone?.getLocalTime(task.created, true)}
                    </td>
                    <td className="date">
                      {timeZone?.getLocalTime(task.dueDate, true)}
                    </td>
                    <td>
                      {task.status === "inProgress"
                        ? "In progress"
                        : task.status}
                    </td>
                    <td>{task.taskType}</td>
                    <td>{task.description}</td>
                    <td className="date">
                      {task.closedDate ? (
                        <span>
                          {task.closedBy.fullName}
                          <br />
                          {timeZone?.getLocalTime(new Date(), true)}
                        </span>
                      ) : null}
                    </td>
                    <td className="image">
                      {task.attachments.map((attachment, idx) => (
                        <img
                          key={idx}
                          src={attachment.thumbUrl}
                          onClick={(e) =>
                            handleAttachmentClicked(e, attachment)
                          }
                        />
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default withNavigation(TasksComponent);
